import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { IconButton } from '@material-ui/core';
import { colors, typography } from '@/styles';

// Values from Figma "Corporate Account - Discovery" (frames 16649:18838 and 16650:20266). These
// render outside the onward-design scope on purpose: inside it, `.onward-design button` strips
// padding, borders and backgrounds, and it outranks a single Emotion class.
const BORDER = '#d9d9d9';
const TEXT_SECONDARY = '#3f3e4a';
const ACCENT = '#2f7a38';
const SUBTLE_BACKGROUND = '#f9f9f9';
const SELECTED_BACKGROUND = '#dff3d2';

export const Library = styled.div`
    ${typography.sansSerif}
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const TabsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

// Shared by the commodity filter chips and the "Assigned to" pills.
const chipCss = css`
    ${typography.sansSerif}
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin: 0;
    padding: 0 5px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    background: ${colors.white.primary};
    color: ${TEXT_SECONDARY};
    font-size: 10px;
    font-weight: 600;
    line-height: 16.7px;
`;

export const FilterChip = styled.button`
    ${chipCss}
    cursor: pointer;

    /* Figma only defines the default state; selected borrows the selected-toggle green. */
    &[aria-pressed='true'] {
        border-color: ${colors.greens.primary};
        background: ${SELECTED_BACKGROUND};
    }
`;

export const AssignmentTag = styled.span`
    ${chipCss}
    white-space: nowrap;
`;

// The bold first word of a chip: the count on a filter chip, the role on an assignment pill.
export const ChipLead = styled.span`
    font-weight: 900;
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const AccentBar = styled.span`
    flex-shrink: 0;
    width: 4px;
    height: 18px;
    border-radius: 2px;
    background: ${ACCENT};
`;

export const SectionTitle = styled.h3`
    ${typography.sansSerif}
    margin: 0;
    color: ${colors.onwardNavy};
    font-size: 15px;
    font-weight: 800;
    line-height: normal;
`;

export const EmptySection = styled.div`
    display: flex;
    align-items: center;
    height: 34.7px;
    padding: 0 12px;
    border: 1px solid #c9c8d1;
    border-radius: 5px;
    background: ${SUBTLE_BACKGROUND};
    color: ${TEXT_SECONDARY};
    font-size: 11.5px;
    line-height: 16.7px;
`;

export const RowCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 14px;
    border: 1px solid ${BORDER};
    border-radius: 4px;
    background: ${colors.white.primary};
`;

export const RowHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 20.8px;
`;

export const RowToggle = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    min-width: 0;
    cursor: pointer;
`;

export const RowName = styled.span`
    color: ${colors.onwardNavy};
    font-size: 12.5px;
    font-weight: 700;
    line-height: 18.1px;
`;

export const RowMeta = styled.span`
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    color: ${colors.greys.primary};
    font-size: 11.5px;
    line-height: 16.7px;
`;

export const CommodityPill = styled.span`
    display: inline-flex;
    align-items: center;
    height: 20.8px;
    padding: 0 10px;
    border: 1px solid ${colors.greys.primary};
    border-radius: 12px;
    background: ${SUBTLE_BACKGROUND};
    color: ${TEXT_SECONDARY};
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.38px;
    line-height: 13.8px;
    white-space: nowrap;
`;

export const RowActions = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 5px;
`;

// Disabled keeps MUI's grey: `.MuiIconButton-root.Mui-disabled` outranks this color.
export const ActionButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'iconColor' })`
    padding: 0;
    color: ${({ iconColor }) => iconColor};
`;

export const ExpandedPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
`;

export const PanelLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

export const PanelLabel = styled.span`
    color: #808080;
    font-size: 10px;
    font-weight: 500;
`;

export const PanelNote = styled.span`
    color: ${colors.greys.primary};
    font-size: 11px;
`;

export const CategorySelectBox = styled.select`
    ${typography.sansSerif}
    margin: 0;
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: ${colors.white.primary};
    color: #000;
    font-size: 11px;
    cursor: pointer;
`;

export const ToggleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const CommodityToggle = styled.button`
    ${typography.sansSerif}
    margin: 0;
    padding: 3px 8px;
    border: 0;
    border-radius: 3px;
    background: #f0f0f0;
    color: #000;
    font-size: 11px;
    line-height: normal;
    cursor: pointer;

    &[aria-pressed='true'] {
        background: ${SELECTED_BACKGROUND};
    }
`;

export const FormStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 320px;
`;

export const FormLabel = styled.span`
    ${typography.sansSerif}
    color: ${colors.greys.secondary};
    font-size: 12px;
    font-weight: 600;
`;
